import { deriveNoteTitle } from '@kanto/shared';
import { useState, type ReactElement, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { errorText } from '../api/client';
import { useArchiveNote, useFolders, useNote, useUpdateNote } from '../api/hooks';
import { AppShell } from '../components/AppShell';
import { NoteEditor, type SaveState } from '../components/NoteEditor';
import { OfflineNotice } from '../components/OfflineNotice';
import { Panel } from '../components/Panel';
import { Spinner } from '../components/Spinner';
import { TagInput } from '../components/TagInput';
import { useT } from '../lib/i18n';

/**
 * One note, full screen.
 *
 * The title is not a field. It is the first line of the body, read by
 * `deriveNoteTitle` — the same function the server uses, out of `@kanto/shared`,
 * so the heading here and the title in the list cannot come to disagree. It
 * updates as the line is typed, with no round trip in between, which is the
 * whole reason that function is shared rather than server-side.
 *
 * Everything that is not the body sits in the header: where the note is filed,
 * what it is tagged, and the way to take it off the list. Each of those saves on
 * the press; only the body waits for the timer, because only the body is being
 * typed.
 */
export default function NotePage(): ReactElement {
  const t = useT();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: note, error, isPending, fetchStatus } = useNote(id ?? '');
  const { data: folderData } = useFolders();
  const update = useUpdateNote();
  const archive = useArchiveNote();

  const [draftTitle, setDraftTitle] = useState<string | null>(null);

  // A path with no identifier is not a note. Sending it back to the list rather
  // than drawing an error: there is nothing to say about it.
  if (id === undefined) return <Navigate to="/notes" replace />;

  if ((fetchStatus === 'paused' || !navigator.onLine) && !note) {
    return (
      <Screen>
        <OfflineNotice />
      </Screen>
    );
  }

  if (isPending) {
    return (
      <Screen>
        <div className="flex h-64 items-center justify-center">
          <Spinner />
        </div>
      </Screen>
    );
  }

  if (error || !note) {
    return (
      <Screen>
        <Panel
          tone="alert"
          title={t('notes.missingTitle')}
          body={errorText(error, t('notes.errorFallback'))}
          icon={
            <path d="M12 8v5M12 16h.01M10.3 3.9 2.4 17a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          }
          action={
            <Link
              to="/notes"
              className="inline-flex min-h-11 items-center rounded-lg bg-accent px-4 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-dim"
            >
              {t('notes.backToList')}
            </Link>
          }
        />
      </Screen>
    );
  }

  const folders = folderData?.folders ?? [];
  // What the heading shows while typing: the draft's own first line, falling
  // back to what the server last derived.
  const heading = draftTitle ?? note.title;

  const state: SaveState = update.isPending
    ? 'saving'
    : update.isError
      ? 'failed'
      : update.isSuccess
        ? 'saved'
        : 'idle';

  return (
    <Screen>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Link
          to="/notes"
          aria-label={t('notes.backToList')}
          className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-ink-400 hover:bg-surface hover:text-ink-900"
        >
          <svg
            viewBox="0 0 24 24"
            className="size-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </Link>

        <h1 className="min-w-0 flex-1 truncate text-2xl font-semibold tracking-tight text-ink-900">
          {heading === '' ? t('notes.untitled') : heading}
        </h1>

        <label className="sr-only" htmlFor="note-folder">
          {t('notes.folder')}
        </label>
        <select
          id="note-folder"
          value={note.folderId ?? ''}
          onChange={(event) =>
            update.mutate({
              id: note.id,
              patch: { folderId: event.target.value === '' ? null : event.target.value },
            })
          }
          className="min-h-9 max-w-40 rounded-lg border border-line bg-surface px-2 text-sm text-ink-500 outline-none focus:border-accent"
        >
          <option value="">{t('notes.inbox')}</option>
          {folders.map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.name}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() =>
            archive.mutate(note.id, { onSuccess: () => void navigate('/notes', { replace: true }) })
          }
          aria-label={t('notes.archive', heading === '' ? t('notes.untitled') : heading)}
          className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-ink-400 transition-colors hover:bg-alert-soft hover:text-alert"
        >
          <svg
            viewBox="0 0 24 24"
            className="size-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 7h18v3H3zM5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9M10 14h4" />
          </svg>
        </button>
      </div>

      <div className="mb-3 flex">
        <TagInput
          tags={note.tags}
          onChange={(tags) => update.mutate({ id: note.id, patch: { tags } })}
        />
      </div>

      <NoteEditor
        // The one event that should reset the draft: a different note. Nothing
        // else writes the server's answer back into the field.
        key={note.id}
        initialBody={note.body}
        state={state}
        // The heading follows the first line as it is typed, with no round trip
        // in between — which is why `deriveNoteTitle` lives in `@kanto/shared`
        // rather than only on the server.
        onDraft={(body) => setDraftTitle(deriveNoteTitle(body))}
        onSave={(body) => update.mutate({ id: note.id, patch: { body } })}
      />
    </Screen>
  );
}

function Screen({ children }: { children: ReactNode }): ReactElement {
  return <AppShell>{children}</AppShell>;
}
